import { TextHeader } from '@components/ui/text-header';
import { useAuth } from '@context/auth';
import MenuSharpIcon from '@mui/icons-material/MenuSharp';
import { AppBar, Box, Button, IconButton, Stack, Toolbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useActiveAccount } from '@queries/account';
import { bgBlur } from '@utils/css';
import { initials } from '@utils/transform';
import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '@routes';
import { AccountDropdown } from './account-dropdown';

const StyledRoot = styled(AppBar)<{ navWidth: number }>(
  ({ navWidth, theme }) => ({
    ...bgBlur({ color: theme.palette.background.default }),
    boxShadow: 'none',
    [theme.breakpoints.up('lg')]: {
      width: `calc(100% - ${navWidth + 1}px)`,
    },
  }),
);

const StyledToolbar = styled(Toolbar)<{
  mobileMinHeight: number;
  desktopMinHeight: number;
}>(({ desktopMinHeight, mobileMinHeight, theme }) => ({
  minHeight: mobileMinHeight,
  [theme.breakpoints.up('lg')]: {
    minHeight: desktopMinHeight,
    padding: theme.spacing(0, 5),
  },
}));

export type HeaderProps = {
  desktopMinHeight: number;
  mobileMinHeight: number;
  navWidth: number;
  onOpenNav: () => void;
};

export const Header: FunctionComponent<HeaderProps> = (props) => {
  const { desktopMinHeight, mobileMinHeight, navWidth, onOpenNav } = props;
  const navigate = useNavigate();
  const { data } = useActiveAccount();
  const { impersonatedAccount, isImpersonator, stopImpersonate } = useAuth();
  const avatarContent = data?.data?.name
    ? initials(data?.data?.name)
        .slice(0, 2)
        .map((initial) => initial.toUpperCase())
        .join('')
    : undefined;
  return (
    <StyledRoot navWidth={navWidth}>
      <StyledToolbar
        desktopMinHeight={desktopMinHeight}
        mobileMinHeight={mobileMinHeight}
      >
        <IconButton
          onClick={onOpenNav}
          sx={{
            color: 'text.primary',
            display: { lg: 'none' },
            mr: 1,
          }}
        >
          <MenuSharpIcon />
        </IconButton>
        {impersonatedAccount && (
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            color="text.primary"
          >
            <TextHeader
              title={impersonatedAccount.name}
              subtitle={impersonatedAccount.gid}
            />
            <Button
              onClick={stopImpersonate}
              color="error"
              variant="outlined"
              sx={{ marginRight: 1 }}
            >
              Stop Impersonating
            </Button>
          </Stack>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {isImpersonator && !impersonatedAccount && (
          <Button
            onClick={() => navigate(routes.impersonator)}
            variant="outlined"
            sx={{ marginRight: 1 }}
          >
            Impersonate
          </Button>
        )}
        <Stack
          direction="row"
          alignItems="center"
          spacing={{
            sm: 1,
            xs: 0.5,
          }}
        >
          <AccountDropdown
            subtitle={data?.data?.email}
            title={data?.data?.name}
            avatarContent={avatarContent}
          />
        </Stack>
      </StyledToolbar>
    </StyledRoot>
  );
};
